/* istanbul ignore file */
import { apiService, useHandleError } from 'framework/api';
import { useAuthenticationProvider } from 'infrastructure/security/authentication/AuthenticationProvider';
import apiServiceAuthenticationProvider from 'infrastructure/security/authentication/apiAuthenticationProvider';
import useUnauthenticatedRequestHandler from 'infrastructure/security/authentication/unauthenticatedRequestHandler';
import {
  BASE_URL,
  CORE_URL,
  DELIVERY_URL,
  DOCUMENT_PREVIEW_URL,
  DOCUMENT_SERVICE_URL,
  EMPLOYEE_AVAILABILITY_URL,
  FILE_DIFFERENCE_URL,
  FINANCE_SERVICE_URL,
  KERNEL_URL,
  PPLM_SERVICE_URL,
  PRIVILEGED_ACCESS_SERVICE_URL,
  RECRUITING_SERVICE_URL,
  SCS_SERVICE_URL,
  SM_SERVICE_URL,
  ST_SERVICE_URL,
  WAKE_API_DAVINCI_URL,
  WAKE_API_JARVIS_URL,
} from '../../api';

const useAuthorizedServices = () => {
  const { isAuthenticated } = useAuthenticationProvider();
  const errorHandler = useHandleError();
  const authenticationProvider = apiServiceAuthenticationProvider();
  const authProvider = isAuthenticated ? authenticationProvider : null;
  const unauthenticatedRequestHandler = useUnauthenticatedRequestHandler();

  return {
    errorHandler,
    authProvider,
    unauthenticatedRequestHandler,
  };
};

export const useBusinessEngineApi = () => {
  return apiService(BASE_URL ?? '', useAuthorizedServices());
};

export const useEmployeeAvailabilityApi = () => {
  return apiService(EMPLOYEE_AVAILABILITY_URL ?? '', useAuthorizedServices());
};

export const useKernelApi = (pathPrefix = '') => {
  return apiService(`${KERNEL_URL}${pathPrefix}`, useAuthorizedServices());
};

export const useCoreApi = () => {
  return apiService(CORE_URL ?? '', useAuthorizedServices());
};

export const useDeliveryApi = () => {
  return apiService(DELIVERY_URL ?? '', useAuthorizedServices());
};

export const useFinanceApi = () => {
  return apiService(FINANCE_SERVICE_URL ?? '', useAuthorizedServices());
};

export const useServiceManagementApi = () => {
  return apiService(SM_SERVICE_URL ?? '', useAuthorizedServices());
};

export const usePeopleManagementApi = () => {
  return apiService(PPLM_SERVICE_URL ?? '', useAuthorizedServices());
};

export const useRecruitmentApi = () => {
  return apiService(RECRUITING_SERVICE_URL ?? '', useAuthorizedServices());
};

export const useCurrentUserApi = () => {
  const { getToken } = useAuthenticationProvider();
  const errorHandler = useHandleError();

  return apiService(KERNEL_URL ?? '', {
    errorHandler,
    authProvider: {
      getAuthHeader: () => ({
        name: 'Authorization',
        value: `Bearer ${getToken()}`,
      }),
    },
    unauthenticatedRequestHandler: null,
  });
};

export const usePrivilegedAccessApi = () => {
  return apiService(PRIVILEGED_ACCESS_SERVICE_URL ?? '', useAuthorizedServices());
};

export const useScsApi = () => {
  return apiService(SCS_SERVICE_URL ?? '', useAuthorizedServices());
};

export const useSyberryTodayApi = () => {
  return apiService(ST_SERVICE_URL ?? '', useAuthorizedServices());
};

export const useDocumentsStorageApi = (pathPrefix = '') => {
  return apiService(`${DOCUMENT_SERVICE_URL}${pathPrefix}`, useAuthorizedServices());
};

export const useDocumentsPreviewApi = () => {
  return apiService(`${DOCUMENT_PREVIEW_URL}`, {
    errorHandler: useHandleError(),
  });
};

export const useFileDifferenceApi = () => {
  return apiService(`${FILE_DIFFERENCE_URL}`, {
    errorHandler: useHandleError(),
  });
};

export const useArtifactStorageApi = () => {
  return useKernelApi('/new');
};

/**
 * A base URL rather than a configured apiService, which is what everything above this
 * hands back.
 *
 * The sleep console posts with a plain fetch on purpose: apiService routes failures
 * through the application-wide error handler, and the only interesting failures there
 * are the scheduler's own refusals, whose message is the thing worth showing. So what
 * the console needs from this layer is the address, not a client.
 *
 * One per SDLC account, because each account owns the API that wakes it. Keyed by a
 * plain string rather than by the console's own union — nothing in infrastructure
 * imports from domain.
 */
export const wakeApiBaseUrl = (environment: string): string | undefined => {
  const baseUrls: Record<string, string | undefined> = {
    davinci: WAKE_API_DAVINCI_URL,
    jarvis: WAKE_API_JARVIS_URL,
  };

  return baseUrls[environment];
};
